/**
 * dialogue_paths.ts — the BANK of versona dialogue paths (2026-09-20).
 *
 * Content for the engine in dialogue_threads.ts. Every thread is keyed on
 * facts the sim already replicates, so the same bank plays differently on
 * every versona: their mood, their personality, the war, the levy, what
 * they carry as hearsay, and — new — what YOU have told them and their
 * neighbours before.
 *
 * Authoring laws (each one cost a bug in topic_dialogue.ts first):
 *   · SPLIT CONJUNCTIONS. `when: [a, b]` is more specific than `[(f)=>a&&b]`
 *     — the count of criteria IS the specificity (most-criteria-wins).
 *   · A thread that ASKS declares `asks`, so an unanswered question can be
 *     recalled ("You never did say…").
 *   · Persona values come from PERSONA_VALUES only: the server composes the
 *     memory text from that closed vocabulary, never from free text.
 *   · Every `goto` names a beat in the same thread (the harness checks).
 */

import {
  tierAtLeast, tierBelow, ignoredSlots, heardAbout,
  type ThreadDef, type ThreadCriterion, type Reply, type PersonaSlot,
} from './dialogue_threads.js';

// ─── Criteria vocabulary ─────────────────────────────────────────────────────

const always: ThreadCriterion = () => true;
const civil: ThreadCriterion = (f) => f.relTier !== 'nemesis' && f.relTier !== 'hostile';
const notAsked = (slot: PersonaSlot): ThreadCriterion => (_f, m) => m.me.asked[slot] === undefined;
const told = (slot: PersonaSlot, value?: string): ThreadCriterion => (_f, m) => (value === undefined ? m.me.told[slot] !== undefined : m.me.told[slot] === value);
const heard = (slot: PersonaSlot): ThreadCriterion => (f, m) => heardAbout(f, m.all, slot) !== null;
const notToldMe = (slot: PersonaSlot): ThreadCriterion => (_f, m) => m.me.told[slot] === undefined;
const visitsAtLeast = (n: number): ThreadCriterion => (_f, m) => m.me.visits >= n;
/** They have talked with you before, on an EARLIER day. (`visits` counts PRIOR conversations: the opener is picked
 *  before this one is noted — see pickOpener.) */
const returning: ThreadCriterion = (f, m) => m.me.visits >= 1 && m.me.lastVisitDay < f.nowDay;
const notOpined = (issue: string): ThreadCriterion => (_f, m) => m.me.opined[issue] === undefined;
const atWar: ThreadCriterion = (f) => f.atWar;
const hasRumor: ThreadCriterion = (f) => f.rumorFacts.length > 0;
const hasNews: ThreadCriterion = (f) => f.townNews !== '';
const bold: ThreadCriterion = (f) => f.boldness >= 60;
const gentle: ThreadCriterion = (f) => f.boldness < 40;
const sociable: ThreadCriterion = (f) => f.sociability >= 55;
const reserved: ThreadCriterion = (f) => f.sociability < 55;

// ─── Reply sets shared by several threads ────────────────────────────────────

const ORIGIN_REPLIES: Reply[] = [
  { label: 'Born on a ship. Never had a ground to be from.', tone: 'plain', goto: 'origin-spacer', effects: [{ t: 'persona', slot: 'origin', value: 'spacer' }] },
  { label: 'The Commons, like most. Nothing exotic.', tone: 'warm', goto: 'origin-commons', effects: [{ t: 'persona', slot: 'origin', value: 'commons' }] },
  { label: 'The frontier. You wouldn\'t know the name.', tone: 'wry', goto: 'origin-frontier', effects: [{ t: 'persona', slot: 'origin', value: 'frontier' }] },
  { label: 'That\'s my business.', tone: 'blunt', goto: 'origin-secret', effects: [{ t: 'persona', slot: 'origin', value: 'secret' }] },
];
const TRADE_REPLIES: Reply[] = [
  { label: 'I trade. Buy where it\'s cheap, sell where it isn\'t.', tone: 'plain', goto: 'trade-trader', effects: [{ t: 'persona', slot: 'trade', value: 'trader' }] },
  { label: 'I fly. Anything with an engine.', tone: 'warm', goto: 'trade-pilot', effects: [{ t: 'persona', slot: 'trade', value: 'pilot' }] },
  { label: 'I fight, when there\'s pay in it.', tone: 'blunt', goto: 'trade-fighter', effects: [{ t: 'persona', slot: 'trade', value: 'fighter' }] },
  { label: 'I make things. Give me a bench and an hour.', tone: 'warm', goto: 'trade-crafter', effects: [{ t: 'persona', slot: 'trade', value: 'crafter' }] },
  { label: 'Honestly? I drift. I haven\'t decided yet.', tone: 'wry', goto: 'trade-wanderer', effects: [{ t: 'persona', slot: 'trade', value: 'wanderer' }] },
];

// ─── The bank ────────────────────────────────────────────────────────────────

export const THREADS: ThreadDef[] = [
  // ═══ TIER 1 — essential: what stands between you comes first ═══════════════
  {
    id: 'confront:nemesis', kind: 'confront', tier: 1,
    when: [(f) => f.relTier === 'nemesis'],
    beats: [
      { id: 'start', say: 'You. You\'ve got nerve, walking up to me like we\'re anything to each other. Say what you came to say and go.', replies: [
        { label: 'I came to put it right.', tone: 'warm', goto: 'amends' },
        { label: 'I\'m not here for you.', tone: 'plain', goto: 'cold' },
        { label: 'Say that again.', tone: 'blunt', goto: 'flare' },
      ] },
      { id: 'amends', say: 'Words. Everyone has words. If you mean it, you know what it costs — make real amends, at a real price, and I\'ll believe it when it\'s in my hands. Until then we\'re done.' },
      { id: 'cold', say: 'Then walk. %town% is wide enough for both of us if you keep to your side of it.' },
      { id: 'flare', say: 'I\'ll say it to the keeper and the whole council table if you like. Everyone here knows what you did. Go.' },
    ],
  },
  {
    id: 'confront:hostile', kind: 'confront', tier: 1,
    when: [(f) => f.relTier === 'hostile'],
    beats: [
      { id: 'start', say: 'I\'ll be plain: I don\'t like you, and I don\'t trust you. %why1%. So whatever this is, keep it short.', replies: [
        { label: 'You\'re right to be angry. I\'m sorry.', tone: 'warm', goto: 'sorry' },
        { label: 'I\'ll keep it short, then.', tone: 'plain', goto: 'short' },
        { label: 'You\'ll get over it.', tone: 'blunt', goto: 'wont' },
      ] },
      { id: 'sorry', say: 'Hm. That\'s more than I expected from you. Say it properly — an apology, face to face — and we\'ll see whether it holds. I make no promises.' },
      { id: 'short', say: 'Good. Ask your question.' },
      { id: 'wont', say: 'No. I don\'t think I will. People here have long memories, traveler — longer than yours, I\'d wager.' },
    ],
  },
  {
    id: 'wary:fear', kind: 'confront', tier: 1,
    when: [civil, (f) => (f.disposition?.fear ?? 0) >= 25],
    beats: [
      { id: 'start', say: 'I\'ll talk. Just — keep your hands where I can see them, all right? I know what you\'re capable of. We all do.', replies: [
        { label: 'You\'ve nothing to fear from me.', tone: 'warm', goto: 'ease' },
        { label: 'Understood.', tone: 'plain', goto: 'ok' },
        { label: 'Good. Remember it.', tone: 'blunt', goto: 'worse' },
      ] },
      { id: 'ease', say: 'Easy to say. But — thank you for saying it. It\'s the first gentle thing I\'ve heard out of you. Amends would say it louder.' },
      { id: 'ok', say: 'Right. What did you want?' },
      { id: 'worse', say: '…I will. So will everyone I tell.' },
    ],
  },
  {
    id: 'debt:obligation', kind: 'opener', tier: 1, once: true,
    when: [civil, (f) => (f.disposition?.obligation ?? 0) >= 20],
    beats: [
      { id: 'start', say: 'Before anything else — I haven\'t forgotten what you did for us. %why1%. People round here say thank-you once and consider it settled. I don\'t. If there\'s something you need to know about %town%, ask me first.', replies: [
        { label: 'Anyone would have done the same.', tone: 'warm', goto: 'modest' },
        { label: 'Then tell me what people won\'t say to a stranger.', tone: 'plain', goto: 'inside', effects: [{ t: 'discover', topic: 'politics' }] },
        { label: 'I\'ll hold you to that.', tone: 'blunt', goto: 'held' },
      ] },
      { id: 'modest', say: 'No. They wouldn\'t. I\'ve lived here long enough to know exactly who would and who wouldn\'t, and the list is short.' },
      { id: 'inside', say: 'All right. Start with the council table — who holds which office, and who they owe. It explains half of what happens in %town% and nobody will tell it to you straight but me.' },
      { id: 'held', say: 'Ha. Do. A debt nobody collects goes sour.' },
    ],
  },
  {
    id: 'grief:volunteer', kind: 'opener', tier: 1,
    when: [civil, (f) => f.feelingTone === 'down', (f) => f.feelingCause !== '', tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'Forgive me if I\'m poor company. %cause%. It sits on me. I keep thinking I\'ll feel it less tomorrow, and then tomorrow comes.', replies: [
        { label: 'I\'m sorry. Tell me about it, if it helps.', tone: 'warm', goto: 'tell' },
        { label: 'It does get lighter. Not smaller — lighter.', tone: 'plain', goto: 'lighter' },
        { label: 'Best to keep busy.', tone: 'blunt', goto: 'busy' },
      ] },
      { id: 'tell', say: 'It does help. Most people cross the street — they don\'t know what to say, so they say nothing, and the nothing is worse. You stood here and asked. I\'ll remember that you asked.' },
      { id: 'lighter', say: 'Lighter. Yes. That\'s the word for it — somebody told you that once, I think, when you needed it. Thank you for passing it on.' },
      { id: 'busy', say: 'That\'s what everyone says. I am busy. It follows me to work.' },
    ],
  },

  // ═══ TIER 2 — contextual: the world, and what they know of you ══════════════
  {
    id: 'word-of-you:trade', kind: 'callback', tier: 2, once: true,
    when: [civil, heard('trade'), notToldMe('trade'), sociable],
    beats: [
      { id: 'start', say: 'Hold on — you\'re the one who\'s %heard:trade%, aren\'t you? %heardby:trade% mentioned you at the canteen. Word gets round %town% faster than the levy collector.', replies: [
        { label: 'That\'s me. What else did they say?', tone: 'wry', goto: 'else' },
        { label: 'It is. Small town.', tone: 'plain', goto: 'small' },
        { label: 'People should mind their own business.', tone: 'blunt', goto: 'mind' },
      ] },
      { id: 'else', say: 'Only that you\'d stopped to talk, which round here counts as a character reference. Most offworlders look straight through us.' },
      { id: 'small', say: 'Smaller than you think. By tomorrow they\'ll know what you had for supper.' },
      { id: 'mind', say: 'In a settlement this size? You ARE our business, traveler. Everything is. It\'s that or watch the ore dry.' },
    ],
  },
  {
    id: 'word-of-you:origin', kind: 'callback', tier: 2, once: true,
    when: [civil, heard('origin'), notToldMe('origin'), sociable],
    beats: [
      { id: 'start', say: 'I know who you are — the traveler who\'s %heard:origin%. %heardby:origin% told half the market. Don\'t look like that; it was kindly meant.', replies: [
        { label: 'I don\'t mind. It\'s true enough.', tone: 'warm', goto: 'true' },
        { label: 'What else is going round about me?', tone: 'wry', goto: 'round' },
      ] },
      { id: 'true', say: 'Good. Truth travels slower than the other thing, so it\'s nice to catch some on the way through.' },
      { id: 'round', say: 'Nothing worth repeating — yet. Give us time. We\'ll invent something if you don\'t supply it.' },
    ],
  },
  {
    id: 'never-said:origin', kind: 'callback', tier: 2, once: true, asks: 'origin',
    when: [civil, returning, (_f, m) => ignoredSlots(m.me).includes('origin')],
    beats: [
      { id: 'start', say: 'You never did say where you hail from, last time. Changed the subject quick as a merchant caught short-weighing. I noticed. So — where?', replies: ORIGIN_REPLIES },
      { id: 'origin-spacer', say: 'Ship-born. That explains the way you stand — like the floor might tilt. No shame in it. Half the founders of %town% were the same.' },
      { id: 'origin-commons', say: 'The Commons! Well, that was easy. I don\'t know why you made such a mystery of it.' },
      { id: 'origin-frontier', say: 'The frontier. Hm. People come off the frontier with stories or with silence, and you\'ve shown me which.' },
      { id: 'origin-secret', say: 'Twice asked, twice refused. All right. I\'ll stop asking — but I won\'t stop wondering, and neither will the rest of %town%.' },
    ],
  },
  {
    id: 'callback:trader', kind: 'callback', tier: 2,
    when: [civil, told('trade', 'trader'), returning],
    beats: [
      { id: 'start', say: 'The trader\'s back. Here\'s something for your ledger, since you asked nothing for it: rations are going for %price% here today. If that\'s dearer than where you came from, you know what to do.', replies: [
        { label: 'That\'s useful. Thank you.', tone: 'warm', goto: 'thanks' },
        { label: 'What else is short in %town%?', tone: 'plain', goto: 'short', effects: [{ t: 'discover', topic: 'market' }] },
      ] },
      { id: 'thanks', say: 'Information\'s the one good that gets cheaper the more of it you give away. Bring me a price from somewhere else next time and we\'re square.' },
      { id: 'short', say: 'Ask me about trade and prices — I\'ll tell you what the stalls are crying out for. A trader who listens eats well here.' },
    ],
  },
  {
    id: 'callback:pilot', kind: 'callback', tier: 2,
    when: [civil, told('trade', 'pilot'), returning],
    beats: [
      { id: 'start', say: 'Our pilot returns. How\'s the sky treating you? I\'ve never been higher than the roof of %workplace%, myself. I can\'t decide if I envy you or pity you.', replies: [
        { label: 'Envy. Definitely envy. You should see it from up there.', tone: 'warm', goto: 'envy' },
        { label: 'It\'s a job. Colder than you\'d think.', tone: 'plain', goto: 'job' },
        { label: 'Pity\'s wasted on me.', tone: 'blunt', goto: 'pity' },
      ] },
      { id: 'envy', say: 'Maybe I will, one day. Take me up when I\'ve finished everything I have to do here — so, never. But it\'s good to be asked.' },
      { id: 'job', say: 'Ha. Everything\'s a job once you do it for pay. Even the sky, apparently.' },
      { id: 'pity', say: 'Noted. Envy it is, then — I\'ve plenty to spare.' },
    ],
  },
  {
    id: 'callback:fighter', kind: 'callback', tier: 2,
    when: [civil, told('trade', 'fighter'), returning, atWar],
    beats: [
      { id: 'start', say: 'You said you fight for pay. Well — %town% is at war with %foe%, and I notice you\'re standing here talking to me rather than out there. Not a criticism. I\'m standing here too.', replies: [
        { label: 'It isn\'t my war.', tone: 'plain', goto: 'notmine', effects: [{ t: 'persona', slot: 'warView', value: 'neutral' }] },
        { label: 'Point me at who\'s hiring.', tone: 'blunt', goto: 'hiring', effects: [{ t: 'persona', slot: 'warView', value: 'hawk' }, { t: 'discover', topic: 'war' }] },
        { label: 'I\'ve seen enough of wars to stay out of this one.', tone: 'warm', goto: 'seen', effects: [{ t: 'persona', slot: 'warView', value: 'dove' }] },
      ] },
      { id: 'notmine', say: 'No. I suppose it isn\'t. I wish I could say the same.' },
      { id: 'hiring', say: 'Ask about the war — I\'ll tell you what I know. The keeper\'s the one with the purse, but the purse is lighter than the speeches.' },
      { id: 'seen', say: 'Then you know more than the ones cheering for it. They\'ve never seen one either — that\'s how they manage the cheering.' },
    ],
  },
  {
    id: 'callback:someone', kind: 'callback', tier: 2, once: true,
    when: [civil, told('purpose', 'someone'), returning],
    beats: [
      { id: 'start', say: 'You said you were looking for someone. Did you find them?', replies: [
        { label: 'Not yet. I\'m still asking.', tone: 'plain', goto: 'notyet' },
        { label: 'I did. It wasn\'t what I hoped.', tone: 'warm', goto: 'found' },
        { label: 'I\'d rather not say.', tone: 'blunt', goto: 'rather' },
      ] },
      { id: 'notyet', say: 'Then ask everyone. Somebody always knows somebody — follow a rumor back far enough and there\'s a person standing at the end of it who was actually there.' },
      { id: 'found', say: 'It rarely is. I\'m sorry. People stay the same in your head and go on changing everywhere else.' },
      { id: 'rather', say: 'Fair. Forget I asked.' },
    ],
  },
  {
    id: 'rumor:volunteer', kind: 'opener', tier: 2,
    when: [civil, hasRumor, sociable],
    beats: [
      { id: 'start', say: 'You\'ll want to hear this. %rumor% — that\'s what\'s going round, anyway.', replies: [
        { label: 'Who told you that?', tone: 'plain', effects: [{ t: 'source' }] },
        { label: 'Do you believe it?', tone: 'warm', goto: 'believe' },
        { label: 'Gossip. I deal in facts.', tone: 'blunt', goto: 'facts' },
      ] },
      { id: 'believe', say: 'Half of it. The trick with a rumor is working out WHICH half. Ask who told me, if you want to weigh it — a story\'s only as good as the mouth it came out of.' },
      { id: 'facts', say: 'Every fact in %town% started as gossip that turned out true. Suit yourself — but you\'ll hear it last.' },
    ],
  },
  {
    id: 'news:volunteer', kind: 'opener', tier: 2,
    when: [civil, hasNews, reserved],
    beats: [
      { id: 'start', say: 'I don\'t usually pass things on. But you should know: %news%.', replies: [
        { label: 'Does that worry you?', tone: 'warm', goto: 'worry' },
        { label: 'What else is happening here?', tone: 'plain', goto: 'else', effects: [{ t: 'discover', topic: 'events' }] },
      ] },
      { id: 'worry', say: 'Everything worries me a little. That\'s how I\'ve lasted. But no — not more than the usual amount. Thank you for asking; most wouldn\'t.' },
      { id: 'else', say: 'Ask me about the goings-on. I keep a quieter ear than most, but it hears plenty.' },
    ],
  },
  {
    id: 'war:ask-view', kind: 'question', tier: 2, once: true, asks: 'warView',
    when: [civil, atWar, notAsked('warView')],
    beats: [
      { id: 'start', say: 'You\'ll have heard we\'re at war with %foe%. Everyone here has an opinion and nobody\'s allowed a quiet one. You\'re from outside — what does it look like from there?', replies: [
        { label: 'Like a fight you have to win now it\'s started.', tone: 'blunt', goto: 'hawk', effects: [{ t: 'persona', slot: 'warView', value: 'hawk' }] },
        { label: 'Like two towns bleeding over a line on a chart.', tone: 'warm', goto: 'dove', effects: [{ t: 'persona', slot: 'warView', value: 'dove' }] },
        { label: 'Like none of my business.', tone: 'plain', goto: 'neutral', effects: [{ t: 'persona', slot: 'warView', value: 'neutral' }] },
      ] },
      { id: 'hawk', say: 'That\'s the keeper\'s line, near enough. Maybe it\'s even right. %casualties% of ours so far — I\'d like to know what the number is that makes it not worth it.' },
      { id: 'dove', say: 'Careful where you say that. But — yes. I think so too, on the nights I\'m honest. Thank you for saying it out loud; I can\'t.' },
      { id: 'neutral', say: 'Lucky you. It\'s very much ours, and it\'s in every price and every empty chair.' },
    ],
  },
  {
    id: 'storm:smalltalk', kind: 'opener', tier: 2,
    when: [civil, (f) => f.weather === 'rain' || f.weather === 'storm' || f.weather === 'snow' || f.weather === 'sandstorm'],
    beats: [
      { id: 'start', say: 'Filthy weather to be out in. You picked a day for it, %player%.', replies: [
        { label: 'I\'ve flown through worse.', tone: 'wry', goto: 'worse' },
        { label: 'Is it often like this here?', tone: 'plain', goto: 'often', effects: [{ t: 'discover', topic: 'weather' }] },
      ] },
      { id: 'worse', say: 'I believe you, and I don\'t want the details. Get under a roof when you can.' },
      { id: 'often', say: 'Ask me about the sky some time — %town% has weather the way other places have politics. Constantly, and nobody agrees about it.' },
    ],
  },
  {
    id: 'neighbor:resident', kind: 'opener', tier: 2, once: true,
    when: [civil, (f) => f.isResident, tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'They tell me you\'ve taken residency. So you\'re one of us now, on paper anyway. It takes longer than paper. But it starts there.', replies: [
        { label: 'What does it take, besides paper?', tone: 'warm', goto: 'takes' },
        { label: 'Paper\'s enough for me.', tone: 'blunt', goto: 'enough' },
      ] },
      { id: 'takes', say: 'Turning up. At the feasts, at the funerals, at the vote. People here forgive a great deal from someone who turns up.' },
      { id: 'enough', say: 'For the levy collector, certainly. For the rest of us — we\'ll see.' },
    ],
  },

  // ═══ TIER 3 — evergreen: they ask about YOU (the Traveler Persona) ══════════
  {
    id: 'q:origin', kind: 'question', tier: 3, once: true, asks: 'origin',
    when: [civil, notAsked('origin')],
    beats: [
      { id: 'start', say: 'I answer a great many questions for someone who\'s been asked none. So: where are you from, %player%? You don\'t stand like anyone local.', replies: ORIGIN_REPLIES },
      { id: 'origin-spacer', say: 'Ship-born. I thought so — you look at the horizon like it\'s a novelty. We get a few of you through. The ones who stay always build something with too many bulkheads.' },
      { id: 'origin-commons', say: 'The Commons. Well, someone has to be. I hear it\'s all noise and notices up there. You\'ll find %town% quiet. Some people can\'t bear it.' },
      { id: 'origin-frontier', say: 'Try me. I\'ve a cousin who went out that way. …No? Well. The frontier\'s wide. Welcome in from it.' },
      { id: 'origin-secret', say: 'Fair enough. A person\'s entitled. But you should know that in %town%, an empty space in a story gets filled — and never with anything flattering.' },
    ],
  },
  {
    id: 'q:trade', kind: 'question', tier: 3, once: true, asks: 'trade',
    when: [civil, notAsked('trade'), visitsAtLeast(1)],
    beats: [
      { id: 'start', say: 'You know my trade — I\'m %job%, and I\'ve told you more about it than you wanted. What\'s yours? What do you actually DO, out there?', replies: TRADE_REPLIES },
      { id: 'trade-trader', say: 'A trader. Then you and I understand each other already: nothing moves unless somebody\'s willing to carry it. Ask me what\'s dear here. I\'ll tell you true.' },
      { id: 'trade-pilot', say: 'A pilot! Then you\'ve seen %town% from above. They say it looks like a spill of lamp-oil at night. I\'d like to see that, once.' },
      { id: 'trade-fighter', say: 'Hm. We\'ve had fighters through before. The good ones are quiet about it, the way you just were. It\'s the loud ones I cross the street for.' },
      { id: 'trade-crafter', say: 'A maker. Good. The settlement runs on people who can fix what breaks. Go and look at %workplace% if you want to see honest work done the slow way.' },
      { id: 'trade-wanderer', say: 'Ha. Honest, at least. Most people your age pretend to a plan. Drift long enough and a place decides for you — this one decided for me.' },
    ],
  },
  {
    id: 'q:purpose', kind: 'question', tier: 3, once: true, asks: 'purpose',
    when: [civil, notAsked('purpose'), visitsAtLeast(2)],
    beats: [
      { id: 'start', say: 'You keep coming back to %town%. People don\'t, as a rule — they pass through. What is it you\'re after here?', replies: [
        { label: 'Work. There\'s always something needs doing.', tone: 'plain', goto: 'work', effects: [{ t: 'persona', slot: 'purpose', value: 'work' }] },
        { label: 'Nothing. I like seeing how people live.', tone: 'warm', goto: 'curiosity', effects: [{ t: 'persona', slot: 'purpose', value: 'curiosity' }] },
        { label: 'Trouble, mostly. It finds me.', tone: 'wry', goto: 'trouble', effects: [{ t: 'persona', slot: 'purpose', value: 'trouble' }] },
        { label: 'I\'m looking for someone.', tone: 'plain', goto: 'someone', effects: [{ t: 'persona', slot: 'purpose', value: 'someone' }] },
      ] },
      { id: 'work', say: 'Then you\'ll never be short of friends here. Ask around — half of us have a job we\'ve been putting off for want of a willing pair of hands.' },
      { id: 'curiosity', say: 'That\'s rarer than you\'d think, and nicer. Most who come look at us like we\'re scenery. Look all you like; we\'re worth it.' },
      { id: 'trouble', say: 'Ha! Well, keep it outside the walls. We\'ve enough of our own and ours has seniority.' },
      { id: 'someone', say: 'Ah. Then ask everyone, and ask them who told THEM. People don\'t vanish here — they just get talked about from further and further away.' },
    ],
  },
  {
    id: 'q:town', kind: 'question', tier: 3, once: true, asks: 'townView',
    when: [civil, notAsked('townView'), visitsAtLeast(1), tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'Be honest with me — I\'ve lived in %town% too long to see it straight. What do you make of the place?', replies: [
        { label: 'I like it. It feels like somewhere people chose.', tone: 'warm', goto: 'fond', effects: [{ t: 'persona', slot: 'townView', value: 'fond' }] },
        { label: 'It\'s a town. It does what towns do.', tone: 'plain', goto: 'plain', effects: [{ t: 'persona', slot: 'townView', value: 'plain' }] },
        { label: 'Honestly? It\'s seen better days.', tone: 'blunt', goto: 'sour', effects: [{ t: 'persona', slot: 'townView', value: 'sour' }] },
      ] },
      { id: 'fond', say: 'Chose. Yes — that\'s it exactly. Nobody ends up in %town% by accident; the road doesn\'t go anywhere else. I\'ll be repeating that, I warn you.' },
      { id: 'plain', say: 'The safest answer there is. I\'ll take it as "no complaints", which from an offworlder is practically a love letter.' },
      { id: 'sour', say: 'It has. You\'re not wrong, and I\'d rather hear it than flattery. But you should have seen it before the founders got the water running — THOSE were the worse days.' },
    ],
  },
  {
    id: 'q:rivals', kind: 'question', tier: 3, once: true, asks: 'rivalView',
    when: [civil, notAsked('rivalView'), (f) => f.rivalDemonym !== '', visitsAtLeast(1)],
    beats: [
      { id: 'start', say: 'You\'ve been about. You\'ll have met %rival% folk as well as us. People here say all sorts about them. What do YOU say?', replies: [
        { label: 'They\'re people. Same worries, different hats.', tone: 'warm', goto: 'open', effects: [{ t: 'persona', slot: 'rivalView', value: 'open' }] },
        { label: 'I\'d keep one hand on my purse.', tone: 'blunt', goto: 'wary', effects: [{ t: 'persona', slot: 'rivalView', value: 'wary' }] },
      ] },
      { id: 'open', say: 'Different hats. Ha. My grandmother said much the same and got shouted down for it. It\'s a thing you can only say if you\'ve actually been there.' },
      { id: 'wary', say: 'Mm. That\'s the general view. I notice it\'s always held most firmly by the ones who\'ve never met any.' },
    ],
  },

  // ═══ TIER 3 — evergreen: WHERE DO YOU STAND? (their opinion, then yours) ════
  {
    id: 'op:levy:for', kind: 'opinion', tier: 3, once: true,
    when: [civil, notOpined('levy'), (f) => f.taxPct > 0, (f) => (f.leanX10 ?? (f.greed >= 55 ? -100 : 100)) >= 0, tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'The levy\'s at %tax% and people grumble. I\'ll say the unpopular thing: it should be higher. There are tables in %town% with nothing on them. What\'s a settlement FOR, if not that? You\'ve seen other places — am I wrong?', replies: [
        { label: 'You\'re not wrong. A town that lets people go hungry isn\'t one.', tone: 'warm', goto: 'agree', effects: [{ t: 'opine', issue: 'levy', stance: 'agree' }] },
        { label: 'I think you are. Take too much and the ones who make things leave.', tone: 'blunt', goto: 'disagree', effects: [{ t: 'opine', issue: 'levy', stance: 'disagree' }] },
        { label: 'Not my place. I don\'t pay it.', tone: 'plain', goto: 'dodge', effects: [{ t: 'opine', issue: 'levy', stance: 'dodge' }] },
      ] },
      { id: 'agree', say: 'Thank you. It\'s lonely, holding that view at the canteen. I might quote you — "even the offworlder says so."' },
      { id: 'disagree', say: 'Hm. That\'s the merchants\' argument, and I\'ve never had a good answer to it, only a loud one. I\'ll think about that. I don\'t promise to change my mind.' },
      { id: 'dodge', say: 'Wise. Wiser than me. I can\'t seem to stop having opinions; it\'s cost me three friendships and a good seat at the canteen.' },
    ],
  },
  {
    id: 'op:levy:against', kind: 'opinion', tier: 3, once: true,
    when: [civil, notOpined('levy'), (f) => f.taxPct > 0, (f) => (f.leanX10 ?? (f.greed >= 55 ? -100 : 100)) < 0, tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'The levy\'s at %tax%. %tax%! I earn it, the council table spends it, and I\'m told to be grateful for the privilege. Tell me that\'s not robbery with paperwork. You\'ve seen other places.', replies: [
        { label: 'It\'s steep. I\'ve seen towns thrive on half that.', tone: 'plain', goto: 'agree', effects: [{ t: 'opine', issue: 'levy', stance: 'agree' }] },
        { label: 'Somebody feeds the ones who can\'t work. That\'s what it buys.', tone: 'warm', goto: 'disagree', effects: [{ t: 'opine', issue: 'levy', stance: 'disagree' }] },
        { label: 'I stay out of other people\'s purses.', tone: 'wry', goto: 'dodge', effects: [{ t: 'opine', issue: 'levy', stance: 'dodge' }] },
      ] },
      { id: 'agree', say: 'THANK you. Half! I knew it. I\'m going to bring that up at the next vote and I\'m going to say a well-travelled person told me.' },
      { id: 'disagree', say: '…Hm. My sister took the ration for a season, years back. I don\'t like remembering that when I\'m angry about the levy. You\'ve made me remember it.' },
      { id: 'dodge', say: 'Ha. The only sensible position. I should try it.' },
    ],
  },
  {
    id: 'op:keeper:for', kind: 'opinion', tier: 3, once: true,
    when: [civil, notOpined('keeper'), (f) => f.mayorName !== '', (f) => !f.isMayor, (f) => f.happy, tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'Say what you like about %mayor% — and people do — but the water runs and the granary\'s full. I\'d vote the same way again. You\'ve met the keeper, or heard of them. Fair judgment?', replies: [
        { label: 'Fair. Results are what count.', tone: 'plain', goto: 'agree', effects: [{ t: 'opine', issue: 'keeper', stance: 'agree' }] },
        { label: 'A full granary can hide a lot. Watch who it\'s full FOR.', tone: 'blunt', goto: 'disagree', effects: [{ t: 'opine', issue: 'keeper', stance: 'disagree' }] },
      ] },
      { id: 'agree', say: 'Exactly what I say. Nobody eats a speech.' },
      { id: 'disagree', say: 'That\'s a sharper thought than I like. …I\'ll keep an eye on it. Ask about the council table, if you want to know who sits where.' },
    ],
  },
  {
    id: 'op:keeper:against', kind: 'opinion', tier: 3, once: true,
    when: [civil, notOpined('keeper'), (f) => f.mayorName !== '', (f) => !f.isMayor, (f) => f.grumpy, tierAtLeast('familiar')],
    beats: [
      { id: 'start', say: 'Between us — %mayor% has held the keeper\'s chair too long. It stops being an office and starts being furniture. We need someone new. Or am I just sour today?', replies: [
        { label: 'You\'re not sour. Chairs should change hands.', tone: 'warm', goto: 'agree', effects: [{ t: 'opine', issue: 'keeper', stance: 'agree' }] },
        { label: 'You\'re sour today. Ask yourself again when you\'ve eaten.', tone: 'wry', goto: 'disagree', effects: [{ t: 'opine', issue: 'keeper', stance: 'disagree' }] },
      ] },
      { id: 'agree', say: 'There. Said by someone with no stake in it — that\'s worth ten of us saying it. There\'s a vote coming; I know where mine goes.' },
      { id: 'disagree', say: 'Ha! …Probably. I\'ll eat, and I\'ll ask myself again, and if I still think it I\'ll know it\'s a real opinion and not just my stomach.' },
    ],
  },
  {
    id: 'op:bold-challenge', kind: 'opinion', tier: 3, once: true,
    when: [civil, bold, notOpined('nerve'), visitsAtLeast(1)],
    beats: [
      { id: 'start', say: 'I\'ll tell you what I think of offworlders generally: you blow in, you take what\'s cheap, you leave, and you call it seeing the world. Change my mind.', replies: [
        { label: 'Some of us. I\'d rather be judged on what I do here.', tone: 'plain', goto: 'judged', effects: [{ t: 'opine', issue: 'nerve', stance: 'disagree' }] },
        { label: 'You\'re right about most of us. I\'m trying to be the exception.', tone: 'warm', goto: 'exception', effects: [{ t: 'opine', issue: 'nerve', stance: 'agree' }] },
        { label: 'And you sit in one town and call it knowing the world.', tone: 'blunt', goto: 'back', effects: [{ t: 'opine', issue: 'nerve', stance: 'disagree' }] },
      ] },
      { id: 'judged', say: 'Then I will. That\'s fair. The ledger\'s open and it\'s empty — fill it how you like.' },
      { id: 'exception', say: 'Everyone\'s trying to be the exception. But you admitted the rule, which most won\'t. That\'s a start.' },
      { id: 'back', say: 'HA. There it is. Finally, one of you with teeth. We\'ll get on, you and I.' },
    ],
  },
  {
    id: 'op:gentle-worry', kind: 'opinion', tier: 3, once: true,
    when: [civil, gentle, notOpined('safety'), visitsAtLeast(1)],
    beats: [
      { id: 'start', say: 'Can I ask you something? You carry yourself like someone who\'s been in danger. Is it — is it as bad out there as they say? Beyond the walls, I mean.', replies: [
        { label: 'It can be. But most days it\'s just quiet and far.', tone: 'warm', goto: 'quiet', effects: [{ t: 'opine', issue: 'safety', stance: 'agree' }] },
        { label: 'Worse. Stay inside the walls.', tone: 'blunt', goto: 'worse', effects: [{ t: 'opine', issue: 'safety', stance: 'disagree' }] },
      ] },
      { id: 'quiet', say: 'Quiet and far. I can hold onto that. Thank you — everyone else either laughs at the question or tries to frighten me.' },
      { id: 'worse', say: 'Oh. …I see. I suppose I did ask. I\'d been thinking of walking out to see the ridge, come the good weather. Perhaps not.' },
    ],
  },
];

/** Every persona slot the bank can ask about — the harness checks each has a question. */
export const ASKED_SLOTS: PersonaSlot[] = ['origin', 'trade', 'purpose', 'townView', 'rivalView', 'warView'];

export { always as _always, tierBelow as _tierBelow };
